import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { CountrySelector } from '../components/CountrySelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011/api';

interface InterestTag {
  id: string;
  name: string;
  category: string;
  icon: string;
}

interface GroupedInterests {
  [category: string]: InterestTag[];
}

export const InterestSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [step, setStep] = useState<'interests' | 'countries'>('interests');
  const [interests, setInterests] = useState<GroupedInterests>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accessToken) {
      navigate('/login');
      return;
    }
    fetchInterests();
    fetchUserInterests();
  }, [accessToken]);

  const fetchInterests = async () => {
    try {
      const response = await axios.get(`${API_URL}/interests`);
      setInterests(response.data);
    } catch (err) {
      setError('Failed to load interests');
    }
  };

  const fetchUserInterests = async () => {
    try {
      const response = await axios.get(`${API_URL}/interests/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userInterests = response.data;
      setSelectedIds(new Set(userInterests.map((i: any) => i.id)));
    } catch (err) {
      // User may not have interests yet, that's okay
    }
  };

  const toggleInterest = (interestId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(interestId)) {
      if (newSelected.size > 3) {
        newSelected.delete(interestId);
      } else {
        setError('Minimum 3 interests required');
        return;
      }
    } else {
      if (newSelected.size < 10) {
        newSelected.add(interestId);
      } else {
        setError('Maximum 10 interests allowed');
        return;
      }
    }
    setSelectedIds(newSelected);
    setError('');
  };

  const handleContinue = async () => {
    if (selectedIds.size < 3) {
      setError('Please select at least 3 interests');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/interests/me`,
        { interestIds: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      // Move to country selection
      setStep('countries');
    } catch (err) {
      setError('Failed to save interests');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryComplete = () => {
    navigate('/video');
  };

  const categoryNames: Record<string, string> = {
    music: 'Music',
    gaming: 'Gaming',
    sports: 'Sports & Fitness',
    travel: 'Travel',
    entertainment: 'Movies & TV',
    technology: 'Technology',
    art: 'Art & Design',
    food: 'Food & Cooking',
    books: 'Books & Reading',
    learning: 'Learning',
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {step === 'interests' ? (
          <>
            <h1 style={styles.title}>What are you interested in?</h1>
            <p style={styles.subtitle}>
              Select 3-10 interests to help us find better matches
            </p>

            <div style={styles.counter}>
              {selectedIds.size} / 10 selected
              {selectedIds.size >= 3 && <span style={styles.checkmark}>✓</span>}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.interestsContainer}>
              {Object.entries(interests).map(([category, tags]) => (
                <div key={category} style={styles.category}>
                  <h3 style={styles.categoryTitle}>{categoryNames[category] || category}</h3>
                  <div style={styles.tagsGrid}>
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => toggleInterest(tag.id)}
                        style={{
                          ...styles.tag,
                          ...(selectedIds.has(tag.id) ? styles.tagSelected : {}),
                        }}
                      >
                        <span style={styles.tagIcon}>{tag.icon}</span>
                        <span>{tag.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleContinue}
              disabled={selectedIds.size < 3 || loading}
              style={{
                ...styles.button,
                ...(selectedIds.size < 3 || loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? 'Saving...' : 'Continue'}
            </button>

            <button onClick={() => navigate('/video')} style={styles.skipButton}>
              Skip for now
            </button>
          </>
        ) : (
          <CountrySelector accessToken={accessToken} onComplete={handleCountryComplete} />
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '2rem 1rem',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-md)',
    padding: '2rem',
    width: '100%',
    maxWidth: '800px',
  },
  title: {
    color: 'var(--color-deep-purple)',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  subtitle: {
    color: 'var(--color-gray-600)',
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  counter: {
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
  },
  checkmark: {
    color: 'green',
    marginLeft: '0.5rem',
  },
  error: {
    background: '#fee',
    color: '#c33',
    padding: '0.75rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  interestsContainer: {
    maxHeight: '500px',
    overflowY: 'auto',
    marginBottom: '1.5rem',
  },
  category: {
    marginBottom: '1.5rem',
  },
  categoryTitle: {
    color: 'var(--color-dark-navy)',
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
  },
  tagsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '0.75rem',
  },
  tag: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.75rem',
    border: '2px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '0.9rem',
  },
  tagSelected: {
    background: 'var(--color-soft-blue)',
    borderColor: 'var(--color-deep-purple)',
    color: 'white',
    fontWeight: '600',
  },
  tagIcon: {
    fontSize: '1.5rem',
    marginBottom: '0.25rem',
  },
  button: {
    width: '100%',
    background: 'var(--color-warm-orange)',
    color: 'white',
    padding: '0.75rem',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  skipButton: {
    width: '100%',
    background: 'transparent',
    color: 'var(--color-gray-600)',
    padding: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    textDecoration: 'underline',
  },
};
